import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, PrinterIcon, UploadIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { Campo, EstadoVacio, Panel } from '../components/ui/Basicos';
import { CodigoQR } from '../components/ui/CodigoQR';
import { ChipEstado, ChipMateria } from '../components/ui/Etiquetas';
import { ChipVencimiento } from '../components/ui/Semaforo';
import { LineaTiempoEtapas } from '../components/expediente/LineaTiempoEtapas';
import { TablaDocumentos } from '../components/expediente/TablaDocumentos';
import { ListaPlazos } from '../components/expediente/ListaPlazos';
import { ListaAudiencias } from '../components/expediente/ListaAudiencias';
import { AbogadosAsignados } from '../components/expediente/AbogadosAsignados';
import { BitacoraPrestamos } from '../components/expediente/BitacoraPrestamos';
import { AsistenteEvidencia } from '../components/documentos/AsistenteEvidencia';
import { useDatos } from '../contexts/DatosContext';
import { useScreenInit } from '../useScreenInit.js';
import { bitacoraDe } from '../data/archivo';
import { audienciasDe, plazosDe, proximoPlazo } from '../utils/consultas';
import { fechaCorta } from '../utils/fechas';
type Pestana = 'etapas' | 'documentos' | 'plazos' | 'audiencias' | 'abogados' | 'bitacora';
export function ExpedienteDetalle() {
  const {
    id = ''
  } = useParams();
  const {
    expedientes,
    plazos,
    audiencias,
    rol,
    marcarPlazoCumplido,
    etapasDe,
    documentosDeExpediente,
    sucursales
  } = useDatos();
  const screenInit = useScreenInit();
  const [pestana, setPestana] = useState<Pestana>(screenInit.pestana as Pestana ?? 'etapas');
  const [subiendo, setSubiendo] = useState(false);
  const expediente = expedientes.find(e => e.id === id);
  const documentos = useMemo(() => expediente ? documentosDeExpediente(expediente.id) : [], [expediente, documentosDeExpediente]);
  const movimientos = useMemo(() => expediente ? bitacoraDe(expediente) : [], [expediente]);
  if (!expediente) {
    return <Panel data-id="element-1384" data-mp="c0jqbe63d47p">
        <EstadoVacio mensaje="No encontramos ese expediente" sugerencia="Vuelve al listado y búscalo por localizador o por nombre de cliente." data-id="element-1385" data-mp="c137utrtxql7" />
      </Panel>;
  }
  const sucursal = sucursales.find(s => s.id === expediente.sucursalId);
  const etapas = etapasDe(expediente.materia);
  const susPlazos = plazosDe(plazos, expediente.id);
  const susAudiencias = audienciasDe(audiencias, expediente.id);
  const proximo = proximoPlazo(plazos, expediente.id);
  const PESTANAS: {
    clave: Pestana;
    etiqueta: string;
    conteo?: number;
  }[] = [{
    clave: 'etapas',
    etiqueta: 'Etapas del proceso',
    conteo: etapas.length
  }, {
    clave: 'documentos',
    etiqueta: 'Documentos',
    conteo: documentos.length
  }, {
    clave: 'plazos',
    etiqueta: 'Plazos y vencimientos',
    conteo: susPlazos.length
  }, {
    clave: 'audiencias',
    etiqueta: 'Audiencias y citas',
    conteo: susAudiencias.length
  }, {
    clave: 'abogados',
    etiqueta: 'Abogados asignados',
    conteo: expediente.abogadoIds.length
  }, {
    clave: 'bitacora',
    etiqueta: 'Bitácora de préstamos',
    conteo: movimientos.length
  }];
  return <>
      <EncabezadoPagina titulo="Detalle de expediente" descripcion={`${expediente.tipoAsunto} · ${expediente.autoridad}`} acciones={<>
            <Link to="/expedientes" className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-1387" data-mp="3fcu7l7sb1kkc">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1388" data-mp="3acy6bgazs78h" />
              Expedientes
            </Link>
            <button type="button" className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1389" data-mp="1tyooz6563ir5">
              <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-1390" data-mp="2f6iawqhaedqo" />
              Imprimir etiqueta
            </button>
          </>} data-id="element-1386" data-mp="2mlixr5smgtos" />

      <Panel data-id="element-1391" data-mp="25euq38f8juy6">
        <div className="flex flex-col gap-6 p-6 lg:flex-row" data-id="element-1392" data-mp="25el1np9u9vul">
          <div className="flex shrink-0 items-start gap-5" data-id="element-1393" data-mp="20gb3d5stif4r">
            <CodigoQR valor={expediente.localizador} tamano={104} data-id="element-1394" data-mp="20hujmweh4q6j" />
            <div data-id="element-1395" data-mp="2y4beluzqirp4">
              <p className="text-2xs font-semibold uppercase tracking-wide text-gris-400" data-id="element-1396" data-mp="2y2ql88p5pesu">
                Localizador
              </p>
              <p className="font-mono text-2xl font-semibold leading-tight text-marino-900 lg:text-3xl" data-id="element-1397" data-mp="2t3477x1i7uiz">
                {expediente.localizador}
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-2" data-id="element-1398" data-mp="2j5zjhss0fr02">
                <ChipMateria materia={expediente.materia} data-id="element-1399" data-mp="1cqywickyqj5m" />
                <ChipEstado estado={expediente.estado} data-id="element-1400" data-mp="1cocdngsmgh83" />
                <span className="text-2xs text-gris-400" data-id="element-1401" data-mp="17qkq0yz0fh9p">
                  Abierto el {fechaCorta(expediente.fechaApertura)}
                </span>
              </div>
              <div className="mt-3" data-id="element-1402" data-mp="3odskhnhfqile">
                <p className="text-2xs font-semibold uppercase tracking-wide text-gris-400" data-id="element-1403" data-mp="3oeu0m6db5v9z">
                  Próximo vencimiento
                </p>
                <div className="mt-1" data-id="element-1404" data-mp="1yhfkudpzvvi">
                  <ChipVencimiento fechaLimite={proximo?.fechaLimite} data-id="element-1405" data-mp="1z99m0ro5ljo" />
                </div>
              </div>
            </div>
          </div>

          <dl className="grid flex-1 gap-x-6 gap-y-4 border-t border-gris-200 pt-5 sm:grid-cols-2 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0" data-id="element-1406" data-mp="6zwwca8v7g83">
            <Campo etiqueta="Cliente" valor={expediente.cliente} data-id="element-1407" data-mp="fzaa3f40uqym" />
            <Campo etiqueta="Contacto" valor={expediente.clienteContacto} data-id="element-1408" data-mp="kxbvi6ox72d9" />
            <Campo etiqueta="Autoridad" valor={expediente.autoridad} data-id="element-1409" data-mp="kv92rewduv4u" />
            <Campo etiqueta="Núm. de expediente judicial" valor={expediente.numeroJudicial} mono data-id="element-1410" data-mp="2hqx29jzngrwo" />
            <Campo etiqueta="Sucursal" valor={sucursal?.nombre ?? ''} data-id="element-1411" data-mp="2mphv2ycko9b3" />
            <Campo etiqueta="Cuantía" valor={expediente.cuantia ?? 'No determinada'} data-id="element-1412" data-mp="2en8u8x2or4cw" />
            <div className="sm:col-span-2" data-id="element-1413" data-mp="2amp104l8gkw1">
              <dt className="text-2xs font-semibold uppercase tracking-wide text-gris-400" data-id="element-1414" data-mp="2ao02tyv4biki">
                Asunto
              </dt>
              <dd className="mt-1 text-sm text-gris-700" data-id="element-1415" data-mp="2oju3julqn1mj">{expediente.resumen}</dd>
            </div>
          </dl>

          <div className="shrink-0 rounded-lg border border-gris-200 bg-gris-50 px-5 py-4 lg:w-64" data-id="element-1416" data-mp="2xmmufwr0dbbu">
            <p className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wide text-gris-500" data-id="element-1417" data-mp="2tljb309r4x5v">
              <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1418" data-mp="37cxx7fyyheh5" />
              Ubicación física
            </p>
            <dl className="mt-3 space-y-2 text-sm" data-id="element-1419" data-mp="32emqhe3yjk35">
              {[['Almacén', expediente.ubicacion.almacen], ['Pasillo', expediente.ubicacion.pasillo], ['Estante', expediente.ubicacion.estante], ['Caja', expediente.ubicacion.caja]].map(([etiqueta, valor]) => <div key={etiqueta} className="flex items-baseline justify-between gap-3" data-id="element-1420" data-mp="3vvq9dhlor8oq">
                  <dt className="text-xs text-gris-500" data-id="element-1421" data-mp="3rfluva4obuc">{etiqueta}</dt>
                  <dd className="font-medium text-marino-900" data-id="element-1422" data-mp="3lyv9au9rl08c">{valor}</dd>
                </div>)}
            </dl>
            <p className="mt-3 border-t border-gris-200 pt-3 text-2xs text-gris-500" data-id="element-1423" data-mp="3r0avwqrlbesu">
              Etapa actual:{' '}
              <span className="font-medium text-gris-700" data-id="element-1424" data-mp="3h01vf0qkf77n">
                {etapas[expediente.etapaActual]?.nombre ?? 'Flujo sin configurar'}
              </span>
            </p>
            {expediente.prestadoA && <p className="mt-2 rounded bg-ambar-50 px-2 py-1.5 text-2xs font-medium text-ambar-700" data-id="element-1425" data-mp="3876zixmzmnuj">
                Prestado a {expediente.prestadoA}
              </p>}
          </div>
        </div>
      </Panel>

      <Panel className="mt-4" data-id="element-1426" data-mp="2y8hif72x17fc">
        <div role="tablist" aria-label="Secciones del expediente" className="flex flex-wrap gap-1 overflow-x-auto scroll-fino border-b border-gris-200 px-3 pt-2" data-id="element-1427" data-mp="2ybn5kgrzhz8f">
          {PESTANAS.map(({
          clave,
          etiqueta,
          conteo
        }) => {
          const activa = pestana === clave;
          return <button key={clave} role="tab" aria-selected={activa} type="button" onClick={() => setPestana(clave)} className={`-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm transition-colors duration-150 ${activa ? 'border-marino-800 font-semibold text-marino-900' : 'border-transparent font-medium text-gris-500 hover:text-marino-700'}`} data-id="element-1428" data-mp="2zskg806ebexu">
                {etiqueta}
                {typeof conteo === 'number' && <span className={`tabular ml-2 rounded px-1.5 py-0.5 text-2xs ${activa ? 'bg-marino-50 text-marino-700' : 'bg-gris-100 text-gris-500'}`} data-id="element-1429" data-mp="274eupb7iplwy">
                    {conteo}
                  </span>}
              </button>;
        })}
        </div>

        <div role="tabpanel" data-id="element-1430" data-mp="2bwfnv2er97cj">
          {pestana === 'etapas' && <LineaTiempoEtapas expediente={expediente} data-id="element-1431" data-mp="2bvxvigv7rd9w" />}
          {pestana === 'documentos' && <>
              {subiendo ? <AsistenteEvidencia expediente={expediente} onCerrar={() => setSubiendo(false)} data-id="element-1432" data-mp="1j7s81mcrza6d" /> : <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gris-200 px-5 py-3" data-id="element-1433" data-mp="1o5a9yssdkp4v">
                  <p className="text-xs text-gris-500" data-id="element-1434" data-mp="vipiztftaf1u">
                    Digitaliza el expediente físico: escanea o fotografía, agrupa páginas y
                    clasifícalas como evidencia con los campos que exige el catálogo.
                  </p>
                  <button type="button" onClick={() => setSubiendo(true)} className="inline-flex h-9 items-center gap-2 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1435" data-mp="2l2qolr5nxuy">
                    <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1436" data-mp="gj9ch3ity7e9" />
                    Subir / Escanear documento
                  </button>
                </div>}
              <TablaDocumentos documentos={documentos} expediente={expediente} data-id="element-1437" data-mp="3l03eb61mo7qo" />
            </>}
          {pestana === 'plazos' && <ListaPlazos plazos={susPlazos} puedeGestionar={rol !== 'Recepción/Archivo'} onCumplir={marcarPlazoCumplido} data-id="element-1438" data-mp="3trnaplism5ih" />}
          {pestana === 'audiencias' && <ListaAudiencias audiencias={susAudiencias} data-id="element-1439" data-mp="31at24213c4xb" />}
          {pestana === 'abogados' && <AbogadosAsignados expediente={expediente} plazos={plazos} data-id="element-1440" data-mp="2tsbopkq3g5z2" />}
          {pestana === 'bitacora' && <BitacoraPrestamos expediente={expediente} movimientos={movimientos} data-id="element-1441" data-mp="2oua5mzso46nl" />}
        </div>
      </Panel>
    </>;
}